import React, { useEffect, useState } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";

import { Key, SorterResult, TableCurrentDataSource, TablePaginationConfig } from "antd/lib/table/interface";

import { Page } from "../../components/page";
import { PageHeader } from "../../components/page_header";
import { DateRange, MarketSelector, Selector } from "../../components/selectors";
import { resolutionOptions, ResolutionSelector } from "../../components/selectors/resolution_selector";
import { TableNotification } from "../../components/table";
import { ReportFormat, ResolutionType } from "../../store/common_types";
import {
  chartFetchRequest,
  chartSearchResultsFetchRequest,
  reportGeneratorTaskCreateRequest,
  tableFetchRequest,
  tableSummaryFetchRequest,
} from "../../store/conversions/actions";
import { CompareType, ConversionItem, MetricType } from "../../store/conversions/types";
import { RootState } from "../../store/root_reducer";
import { enumParams } from "../../utils/enum_params";
import { MomentParam } from "../../utils/moment_param";
import { rangeResolutionLimit, resolutionUnitMap } from "../../utils/resolution";

import { ConversionsChart, ConversionsTable } from "./components";
import styles from "./styles.module.scss";

const compareOptions: Array<CompareType> = ["1d", "1w", "1m", "1y"];
const metricOptions: Array<MetricType | "search_results"> = [
  "searches",
  "clicks",
  "unique_clicks",
  "bookings",
  "sales",
  "btr",
  "ctr",
  "str",
  "percent_successful_responses",
  "percent_errors_responses",
  "percent_zeros_responses",
  "percent_timeouts",
  "productivity",
  "response_duration",
  "search_results",
];

export const Conversions: React.FC = () => {
  const { t } = useTranslation(["conversions_page", "common"]);

  const [refresh, setRefresh] = useState(true);
  const conversionsState = useSelector((state: RootState) => state.Conversions);
  const markets = useSelector((state: RootState) => state.Markets.markets);
  const gateId = useSelector((state: RootState) => state.User.selectedGate.id);

  const [commonState, setCommonState] = useQueryParams({
    from: withDefault(MomentParam, moment.utc().startOf("day").subtract(1, "week").add(1, "day")),
    to: withDefault(MomentParam, moment.utc().startOf("day")),
    market: withDefault(StringParam, undefined),
    resolution: withDefault(enumParams<ResolutionType>(StringParam, resolutionOptions), "1d"),
  });

  const [tableState, setTableState] = useQueryParams({
    ordering: withDefault(StringParam, "-timestamp"),
    page: withDefault(NumberParam, 1),
    pageSize: withDefault(NumberParam, 10),
  });

  const [chartState, setChartState] = useQueryParams({
    metric: withDefault(enumParams<MetricType | "search_results">(StringParam, metricOptions), "searches"),
    compare: withDefault(enumParams<CompareType>(StringParam, compareOptions), "1d"),
  });

  const dispatch = useDispatch();
  useEffect(() => {
    setCommonState((state) => state, "replaceIn");
    setTableState((state) => state, "replaceIn");
    setChartState((state) => state, "replaceIn");
  }, [setCommonState, setTableState, setChartState]);

  useEffect(() => {
    const handler = chartState.metric === "search_results" ? chartSearchResultsFetchRequest : chartFetchRequest;
    dispatch(handler({ ...commonState, ...chartState, metric: chartState.metric as MetricType, gateId }));
  }, [dispatch, commonState, chartState, gateId, refresh]);

  useEffect(() => {
    dispatch(tableFetchRequest({ ...commonState, ...tableState, gateId }));
  }, [dispatch, commonState, tableState, gateId, refresh]);
  useEffect(() => {
    dispatch(tableSummaryFetchRequest({ ...commonState, gateId }));
  }, [dispatch, commonState, gateId, refresh]);

  const handleDateRangeSelect = (dateRange: DateRange) => {
    const rangeDiffDays = dateRange.to.diff(dateRange.from, "days");
    setCommonState((state) => ({
      ...state,
      ...dateRange,
      resolution: rangeResolutionLimit(rangeDiffDays, state.resolution),
    }));
    setTableState((state) => ({ ...state, page: 1 }), "replaceIn");
  };

  const handleDownloadReport = (reportFormat: ReportFormat) => {
    dispatch(reportGeneratorTaskCreateRequest({ ...commonState, reportFormat, ordering: tableState.ordering, gateId }));
  };

  const handleTableChange = (
    { current, pageSize }: TablePaginationConfig,
    filters: Record<string, (Key | boolean)[] | null>,
    sorter: SorterResult<ConversionItem> | SorterResult<ConversionItem>[],
    { action }: TableCurrentDataSource<ConversionItem>
  ) => {
    switch (action) {
      case "paginate": {
        if (current && pageSize)
          setTableState((state) => ({ ...state, page: state.pageSize !== pageSize ? 1 : current, pageSize }));
        break;
      }
      case "sort":
        if (!Array.isArray(sorter)) {
          const { field, order } = sorter;
          const ordering = `${order === "descend" ? "-" : ""}${field}`;
          setTableState((state) => ({ ...state, ordering }));
        }
        break;
      default:
        break;
    }
  };

  return (
    <Page title={t("title")} description={t("description")} reportTTL={365} report="conversions">
      <PageHeader
        onReload={() => setRefresh((state) => !state)}
        isLoading={conversionsState.dataIsLoading}
        onDownloadReport={handleDownloadReport}
        onDateRangeSelect={handleDateRangeSelect}
        reportDownloadDisabled={conversionsState.count === 0}
        dateRange={commonState}
      >
        <MarketSelector
          markets={markets}
          value={commonState.market}
          onSelect={(value) => setCommonState((state) => ({ ...state, market: value }))}
        />
        <ResolutionSelector
          resolution={commonState.resolution}
          rangeDiffDay={commonState.to.diff(commonState.from, "days")}
          onSelect={(resolution) => {
            setCommonState((state) => ({ ...state, resolution }));
            setTableState((state) => ({ ...state, page: 1 }));
          }}
        />
        <Selector
          value={chartState.metric}
          className={styles.metricSelector}
          title={t("metricSelector.title")}
          placeholder={t("metricSelector.placeholder")}
          onSelect={(value) => setChartState((state) => ({ ...state, metric: value as MetricType }))}
          options={metricOptions.map((option) => ({ value: option, title: t(`metricSelector.options.${option}`) }))}
        />
        <Selector
          value={chartState.compare}
          title={t("compareSelector.title")}
          disabled={chartState.metric === "search_results"}
          placeholder={t("compareSelector.placeholder")}
          onSelect={(value) => setChartState((state) => ({ ...state, compare: value as CompareType }))}
          options={compareOptions.map((option) => ({ value: option, title: t(`compareSelector.options.${option}`) }))}
        />
      </PageHeader>
      <ConversionsChart
        items={conversionsState.chartItems}
        searchResultsItems={conversionsState.chartSearchResultsItems}
        metric={chartState.metric as MetricType}
        compare={chartState.compare as CompareType}
        resolution={commonState.resolution}
        rangeFrom={commonState.from}
        rangeTo={commonState.to}
        loading={conversionsState.chartDataIsLoading || conversionsState.chartSearchResultsDataIsLoading}
      />
      <TableNotification />
      <ConversionsTable
        timestampFormat={resolutionUnitMap[commonState.resolution].tooltipFormat as string}
        loading={conversionsState.dataIsLoading}
        total={conversionsState.count}
        dataSource={conversionsState.items}
        currentPage={tableState.page}
        pageSize={tableState.pageSize}
        ordering={tableState.ordering}
        onChange={handleTableChange}
        summary={conversionsState.summary}
      />
    </Page>
  );
};
